import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../../redux/userSlice";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isAuthenticated, error: reduxError } = useSelector((state) => state.user);

  const showToast = useCallback((message, type = "success") => {
    const toast = document.createElement("div");
    toast.className = `login-toast login-toast-${type}`;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add("login-toast-show"), 100);
    setTimeout(() => {
      toast.classList.remove("login-toast-show");
      setTimeout(() => document.body.removeChild(toast), 300);
    }, 3000);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [navigate, isAuthenticated]);

  useEffect(() => {
    if (reduxError) {
      showToast(reduxError, "error");
    }
  }, [reduxError, showToast]);

  const [loginFormData, setLoginFormData] = useState({
    phone: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    phone: "",
    password: "",
  });

  const [touched, setTouched] = useState({
    phone: false,
    password: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Validation functions
  const validatePhone = (phone) => {
    if (!phone) {
      return "Phone number is required";
    }
    if (!/^\d{10}$/.test(phone)) {
      return "Phone number must be exactly 10 digits";
    }
    return "";
  };

  const validatePassword = (password) => {
    if (!password) {
      return "Password is required";
    }
    if (password.length < 6) {
      return "Password must be at least 6 characters";
    }
    if (password.length > 50) {
      return "Password must be less than 50 characters";
    }
    return "";
  };

  // Real-time validation
  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, loginFormData[field]);
  };

  const validateField = (field, value) => {
    let error = "";
    switch (field) {
      case "phone":
        error = validatePhone(value);
        break;
      case "password":
        error = validatePassword(value);
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [field]: error }));
    return error === "";
  };

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    
    // Only allow digits for phone
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "");
      setLoginFormData((prev) => ({ ...prev, [name]: digitsOnly }));
      if (touched.phone) {
        validateField(name, digitsOnly);
      }
    } else {
      setLoginFormData((prev) => ({ ...prev, [name]: value }));
      if (touched[name]) {
        validateField(name, value);
      }
    }
  };

  const validateForm = () => {
    const phoneValid = validateField("phone", loginFormData.phone);
    const passwordValid = validateField("password", loginFormData.password);
    
    setTouched({ phone: true, password: true });
    
    return phoneValid && passwordValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({ phone: "", password: "" });

    try {
      await dispatch(login(loginFormData)).unwrap();
      // Success handled by useEffect redirect
    } catch (error) {
      showToast(error || "Login failed. Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <Helmet>
        <title>Login - RTS Books</title>
      </Helmet>
      <style>{`
        * {
          box-sizing: border-box;
        }

        @keyframes loginSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes loginSlideIn {
          from { 
            opacity: 0; 
            transform: translateX(100%); 
          }
          to { 
            opacity: 1; 
            transform: translateX(0); 
          }
        }

        @keyframes loginFadeIn {
          from { 
            opacity: 0; 
            transform: translateY(20px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        @keyframes loginGradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes loginShake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }

        .login-toast {
          position: fixed;
          top: 20px;
          right: -300px;
          background: #fff;
          color: #333;
          padding: 15px 20px;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          z-index: 10000;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          max-width: 350px;
          font-weight: 500;
          font-size: 0.95rem;
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .login-toast.login-toast-success { 
          border-left: 4px solid #059669;
        }

        .login-toast.login-toast-error { 
          border-left: 4px solid #dc2626;
        }

        .login-toast.login-toast-show {
          right: 20px;
          animation: loginSlideIn 0.3s ease;
        }

        .login-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
          background-size: 400% 400%;
          animation: loginGradient 15s ease infinite;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
            'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
          padding: 20px;
          position: relative;
          overflow: hidden;
        }

        .login-container::before {
          content: "";
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 50px 50px;
          animation: loginGradient 20s ease infinite;
        }

        .login-container::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.05);
        }

        .login-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 50px 40px;
          max-width: 480px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3), 
                      0 0 0 1px rgba(255, 255, 255, 0.1),
                      inset 0 1px 0 rgba(255, 255, 255, 0.6);
          position: relative;
          z-index: 1;
          animation: loginFadeIn 0.6s ease;
        }

        .login-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #667eea);
          background-size: 300% 100%;
          border-radius: 24px 24px 0 0;
          animation: loginGradient 3s ease-in-out infinite;
        }

        .login-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .login-title {
          margin: 0 0 10px 0;
          font-size: 2.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          background-size: 200% 200%;
          animation: loginGradient 3s ease infinite;
        }

        .login-subtitle {
          margin: 0;
          color: #64748b;
          font-size: 1rem;
          font-weight: 400;
        }

        .login-form-group {
          margin-bottom: 24px;
          position: relative;
        }

        .login-label {
          display: block;
          margin-bottom: 10px;
          color: #334155;
          font-size: 0.95rem;
          font-weight: 600;
        }

        .login-label.required::after {
          content: " *";
          color: #dc2626;
        }

        .login-input-wrapper {
          position: relative;
        }

        .login-input {
          width: 100%;
          padding: 16px 18px;
          border: 2px solid #e1e5e9;
          border-radius: 14px;
          font-size: 1rem;
          font-family: inherit;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: #fff;
          color: #1e293b;
        }

        .login-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
          transform: translateY(-1px);
        }

        .login-input.error {
          border-color: #dc2626;
          animation: loginShake 0.5s ease;
        }

        .login-input.error:focus {
          box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.1);
        }

        .login-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          background: #f8f9fa;
        }

        .login-input::placeholder {
          color: #94a3b8;
        }

        .login-error-message {
          color: #dc2626;
          font-size: 0.85rem;
          margin-top: 6px;
          display: flex;
          align-items: center;
          gap: 4px;
          animation: loginFadeIn 0.3s ease;
        }

        .login-error-icon {
          font-size: 1rem;
        }

        .login-password-toggle {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.3rem;
          padding: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          opacity: 0.6;
          color: #64748b;
        }

        .login-password-toggle:hover {
          opacity: 1;
          transform: translateY(-50%) scale(1.1);
          color: #667eea;
        }

        .login-password-toggle:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .login-submit-btn {
          width: 100%;
          padding: 18px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          background-size: 200% 100%;
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
          font-family: inherit;
          margin-top: 10px;
        }

        .login-submit-btn::before {
          content: "";
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          transition: left 0.5s ease;
        }

        .login-submit-btn:hover:not(:disabled)::before {
          left: 100%;
        }

        .login-submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
          animation: loginGradient 2s ease infinite;
        }

        .login-submit-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .login-submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .login-spinner {
          width: 24px;
          height: 24px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          animation: loginSpin 1s linear infinite;
          margin: 0 auto;
        }

        .login-footer {
          margin-top: 30px;
          text-align: center;
          color: #64748b;
          font-size: 0.9rem;
        }

        .login-footer-link {
          color: #667eea;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .login-footer-link:hover {
          color: #764ba2;
          text-decoration: underline;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .login-card {
            padding: 40px 30px;
            border-radius: 20px;
          }

          .login-title {
            font-size: 2rem;
          }

          .login-subtitle {
            font-size: 0.9rem;
          }

          .login-input {
            padding: 14px 16px;
            font-size: 0.95rem;
          }

          .login-submit-btn {
            padding: 16px;
            font-size: 1rem;
          }
        }

        @media (max-width: 480px) {
          .login-container {
            padding: 15px;
          }

          .login-card {
            padding: 35px 25px;
            border-radius: 20px;
          }

          .login-title {
            font-size: 1.8rem;
          }

          .login-subtitle {
            font-size: 0.85rem;
            margin-bottom: 25px;
          }

          .login-form-group {
            margin-bottom: 20px;
          }

          .login-input {
            padding: 14px 16px;
            font-size: 0.95rem;
          }

          .login-submit-btn {
            padding: 16px;
            font-size: 1rem;
          }
        }

        @media (max-width: 360px) {
          .login-card {
            padding: 30px 20px;
          }

          .login-title {
            font-size: 1.6rem;
          }

          .login-input {
            padding: 12px 14px;
            font-size: 0.9rem;
          }
        }

        @media (min-width: 1200px) {
          .login-card {
            max-width: 520px;
            padding: 60px 50px;
          }
        }
      `}</style>

      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h2 className="login-title">Welcome Back</h2>
            <p className="login-subtitle">Sign in to continue to your account</p>
          </div>
          
          <form onSubmit={handleSubmit} noValidate>
            <div className="login-form-group">
              <label className="login-label required" htmlFor="phone">
                Phone Number
              </label>
              <div className="login-input-wrapper">
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={loginFormData.phone}
                  onChange={handleLoginInputChange}
                  onBlur={() => handleBlur("phone")}
                  placeholder="Enter your 10-digit phone number"
                  disabled={isLoading}
                  maxLength={10}
                  className={`login-input ${errors.phone && touched.phone ? "error" : ""}`}
                  aria-invalid={errors.phone && touched.phone}
                  aria-describedby={errors.phone && touched.phone ? "phone-error" : undefined}
                />
              </div>
              {errors.phone && touched.phone && (
                <div className="login-error-message" id="phone-error" role="alert">
                  <span className="login-error-icon">⚠️</span>
                  {errors.phone}
                </div>
              )}
            </div>

            <div className="login-form-group">
              <label className="login-label required" htmlFor="password">
                Password
              </label>
              <div className="login-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={loginFormData.password}
                  onChange={handleLoginInputChange}
                  onBlur={() => handleBlur("password")}
                  placeholder="Enter your password"
                  disabled={isLoading}
                  className={`login-input ${errors.password && touched.password ? "error" : ""}`}
                  style={{ paddingRight: "50px" }}
                  aria-invalid={errors.password && touched.password}
                  aria-describedby={errors.password && touched.password ? "password-error" : undefined}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  disabled={isLoading}
                  className="login-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={0}
                >
                  {showPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
              {errors.password && touched.password && (
                <div className="login-error-message" id="password-error" role="alert">
                  <span className="login-error-icon">⚠️</span>
                  {errors.password}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="login-submit-btn"
              aria-busy={isLoading}
            >
              {isLoading ? (
                <div className="login-spinner"></div>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>
              Don't have an account?{" "}
              <a href="/register" className="login-footer-link">
                Sign up
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
